const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.voicescience.org'
const AUTH_BASE = import.meta.env.VITE_AUTH_BASE ?? 'https://www.voicescience.org'
const ANALYTICS_BASE = import.meta.env.VITE_ANALYTICS_BASE ?? 'https://analytics.voicescience.org'
const TEACHER_API_BASE = import.meta.env.VITE_TEACHER_API_BASE ?? 'https://teacher.voicescience.org'
const STUDIO_API_BASE = import.meta.env.VITE_STUDIO_API_BASE ?? 'https://studio-api.voicescience.org'

type FetchOptions = RequestInit & {
  params?: Record<string, string | number | boolean | undefined>
}

async function request<T>(base: string, path: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...init } = options
  let url = `${base}${path}`

  if (params) {
    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) searchParams.set(key, String(value))
    }
    const qs = searchParams.toString()
    if (qs) url += `?${qs}`
  }

  const res = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init.headers,
    },
    ...init,
  })

  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new ApiError(res.status, body || res.statusText)
  }

  return res.json() as Promise<T>
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

export const api = {
  get: <T>(path: string, options?: FetchOptions) => request<T>(API_BASE, path, options),
  post: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    request<T>(API_BASE, path, { method: 'POST', body: JSON.stringify(body), ...options }),
}

export const authApi = {
  get: <T>(path: string, options?: FetchOptions) => request<T>(AUTH_BASE, path, options),
  post: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    request<T>(AUTH_BASE, path, { method: 'POST', body: JSON.stringify(body), ...options }),
  put: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    request<T>(AUTH_BASE, path, { method: 'PUT', body: JSON.stringify(body), ...options }),
  patch: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    request<T>(AUTH_BASE, path, { method: 'PATCH', body: JSON.stringify(body), ...options }),
  delete: <T>(path: string, options?: FetchOptions) =>
    request<T>(AUTH_BASE, path, { method: 'DELETE', ...options }),
}

export const analyticsApi = {
  get: <T>(path: string, options?: FetchOptions) => request<T>(ANALYTICS_BASE, path, options),
}

export const teacherApi = {
  get: <T>(path: string, options?: FetchOptions) => request<T>(TEACHER_API_BASE, path, options),
  post: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    request<T>(TEACHER_API_BASE, path, { method: 'POST', body: JSON.stringify(body), ...options }),
}

export const studioApi = {
  get: <T>(path: string, options?: FetchOptions) => request<T>(STUDIO_API_BASE, path, options),
  post: <T>(path: string, body?: unknown, options?: FetchOptions) =>
    request<T>(STUDIO_API_BASE, path, { method: 'POST', body: JSON.stringify(body), ...options }),
}
